// Mock data for SocialConvert Landing Page

export const heroData = {
  headline: "Turn Your Social Media Into a High-Performance Website.",
  subheadline: "Custom-built websites that convert followers into clients, bookings, and wins.",
  primaryCTA: "Build My Site",
  secondaryCTA: "See Live Examples",
  stats: [
    { value: "98%", label: "Client Satisfaction" },
    { value: "48hr", label: "Avg. Turnaround" },
    { value: "100%", label: "Custom Built" }
  ]
};

export const problemsData = [
  {
    id: 1,
    icon: "TrendingDown",
    title: "Followers Aren't Converting",
    description: "You've got the audience, but they're not becoming paying clients."
  },
  {
    id: 2,
    icon: "Timer",
    title: "Generic Tools Slow You Down",
    description: "Cookie-cutter link-in-bio tools don't match your hustle or brand."
  },
  {
    id: 3,
    icon: "CalendarX",
    title: "Missed Bookings = Missed Wins",
    description: "Without seamless booking, you're leaving money on the table."
  },
  {
    id: 4,
    icon: "Users",
    title: "Hard to Stand Out",
    description: "In a crowded digital space, blending in means falling behind."
  }
];

export const solutionsData = [
  {
    id: 1,
    icon: "Palette",
    title: "Fully Customized",
    description: "Brand-driven websites built exclusively for your unique identity."
  },
  {
    id: 2,
    icon: "Zap",
    title: "Speed & Clarity",
    description: "Lightning-fast load times designed for mobile-first audiences."
  },
  {
    id: 3,
    icon: "Target",
    title: "Conversion Optimized",
    description: "Every element engineered to turn visitors into customers."
  },
  {
    id: 4,
    icon: "Sparkles",
    title: "Clean & Professional",
    description: "Look confident, credible, and ready for the spotlight."
  }
];

export const featuresData = [
  {
    id: 1,
    icon: "Globe",
    title: "Custom Branded Domain",
    description: "Your own domain or optimized bio link that screams professionalism."
  },
  {
    id: 2,
    icon: "Smartphone",
    title: "Mobile-First Design",
    description: "Fast-loading pages built for how your audience actually browses."
  },
  {
    id: 3,
    icon: "Calendar",
    title: "Booking Integration",
    description: "Seamless scheduling, contact forms, or checkout built right in."
  },
  {
    id: 4,
    icon: "Image",
    title: "Highlight Reels",
    description: "Showcase your best images, clips, and moments beautifully."
  },
  {
    id: 5,
    icon: "Share2",
    title: "Social Media Embeds",
    description: "Connect Instagram, TikTok, YouTube and more in one place."
  },
  {
    id: 6,
    icon: "BarChart3",
    title: "Performance Analytics",
    description: "Track clicks, conversions, and growth with real-time insights."
  },
  {
    id: 7,
    icon: "RefreshCw",
    title: "Easy Updates",
    description: "Update your site anytime without any tech stress or coding."
  }
];

export const stepsData = [
  {
    step: 1,
    title: "Share Your Vision",
    description: "Tell us your brand, goals, and style. We listen, we learn, we plan."
  },
  {
    step: 2,
    title: "We Build Your Site",
    description: "Our team designs your personalized high-performance website."
  },
  {
    step: 3,
    title: "Launch & Convert",
    description: "You go live and start turning followers into real results."
  }
];

export const audienceData = [
  { id: 1, icon: "Dumbbell", label: "Athletes & Sports Creators" },
  { id: 2, icon: "Heart", label: "Fitness Professionals" },
  { id: 3, icon: "Scissors", label: "Barbers & Stylists" },
  { id: 4, icon: "Mic", label: "Podcasters & Influencers" },
  { id: 5, icon: "Rocket", label: "Entrepreneurs & Hustlers" },
  { id: 6, icon: "Briefcase", label: "Service Providers" }
];

export const testimonialsData = [
  {
    id: 1,
    name: "Marcus Thompson",
    role: "Fitness Coach",
    avatar: "MT",
    quote: "My booking rate went up 340% in the first month. This site pays for itself every week.",
    metric: "+340% Bookings"
  },
  {
    id: 2,
    name: "Sarah Rodriguez",
    role: "Personal Trainer",
    avatar: "SR",
    quote: "Finally a website that matches my energy. Clients love how easy it is to book sessions.",
    metric: "50+ New Clients"
  },
  {
    id: 3,
    name: "DeShawn Williams",
    role: "Sports Creator",
    avatar: "DW",
    quote: "Went from a basic link-in-bio to a full brand experience. Game changer for my sponsorships.",
    metric: "3x Sponsorship Deals"
  }
];

export const pricingData = [
  {
    id: 1,
    name: "Starter",
    price: "$497",
    period: "one-time",
    description: "Perfect for getting started with a professional online presence.",
    features: [
      "Custom 1-page website",
      "Mobile-responsive design",
      "Social media links",
      "Contact form",
      "1 round of revisions"
    ],
    cta: "Get Started",
    highlighted: false
  },
  {
    id: 2,
    name: "Pro",
    price: "$997",
    period: "one-time",
    description: "For serious creators ready to maximize conversions.",
    features: [
      "Custom multi-page website",
      "Booking system integration",
      "Social media embeds",
      "Analytics dashboard",
      "3 rounds of revisions",
      "Priority support"
    ],
    cta: "Go Pro",
    highlighted: true
  },
  {
    id: 3,
    name: "Elite",
    price: "$1,997",
    period: "one-time",
    description: "Full-service solution for established brands.",
    features: [
      "Everything in Pro",
      "E-commerce integration",
      "Custom animations",
      "SEO optimization",
      "Unlimited revisions",
      "30-day support included"
    ],
    cta: "Go Elite",
    highlighted: false
  }
];

export const aboutData = {
  name: "Pettijohn Digital",
  founder: "Built by someone who gets it.",
  headline: "I Know How Athletes & Creators Move Online",
  intro: "I'm a Florida A&M University business student with a background in data analytics, sports media, and athlete branding. I've worked at the intersection of sports, NIL, content creation, and digital marketing — not from the sidelines, but in the game.",
  experience: [
    "Worked directly with athletes on NIL branding and digital presence",
    "Collaborated with podcasters and content creators on growth strategy",
    "Built digital solutions for small businesses in the sports and lifestyle space",
    "Studied how followers behave — and why most profiles fail to convert"
  ],
  philosophy: "I don't build generic websites. I build strategic digital platforms that feel like an extension of your brand — designed to convert your social media traffic into bookings, leads, and real results.",
  approach: [
    { title: "Strategic, Not Templated", description: "Every site is custom-built around your goals, brand, and audience." },
    { title: "One-on-One Partnership", description: "I handle every project personally — from strategy to launch." },
    { title: "Sports Culture Native", description: "I understand how athletes, creators, and fans move online." },
    { title: "Results-Focused", description: "Performance, clarity, and conversion — not just aesthetics." }
  ],
  cta: "Let's Build Something That Wins"
};

export const footerData = {
  brand: "DP Studios",
  tagline: "Turn followers into wins.",
  links: [
    { label: "Features", href: "#features" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "About", href: "#about" },
    { label: "Pricing", href: "#pricing" },
    { label: "Contact", href: "#contact" }
  ],
  social: [
    { platform: "Instagram", href: "#" },
    { platform: "Twitter", href: "#" },
    { platform: "LinkedIn", href: "#" }
  ]
};
